/**
 * DataTable — the parameterized semantic table for the product surface.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * 106 files in src/ hand-roll a <thead>. The header cell alone has twelve
 * mutually incompatible class strings in the tree today, the four most common
 * being:
 *     p-2 text-left font-semibold text-gray-700                       (33×)
 *     px-4 py-2 text-left text-xs font-medium text-neutral-500 uppercase (17×)
 *     px-4 py-3 text-center text-sm font-semibold text-gray-700       (16×)
 *     px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase … (12×)
 * Most carry no `dark:` variant at all, which is why tables go unreadable in
 * dark mode. That drift is the icon-button story (see ../mono/iconButton.ts)
 * repeated at twenty times the scale.
 *
 * ── What this is NOT ───────────────────────────────────────────────────────
 * Not a replacement for ../collection/EntityTable. That one is a *virtualized*,
 * div-based, full-height entity grid with shift-range selection, resizable
 * columns and keyboard navigation, mounted inside a CollectionContext. It is
 * the right tool for the big list pages and stays. DataTable is the other job:
 * a semantic <table> of tens-not-thousands of rows, rendered inline on a page,
 * with no provider, no measurement and no virtualization. Reach for EntityTable
 * when the row count is unbounded; reach for this one otherwise.
 *
 * ── Parameters, not variants ───────────────────────────────────────────────
 * Everything a caller used to express by rewriting the markup is a prop:
 * `showHeader`, `density`, `rule`, `zebra`, `stickyHeader`, `selectable`,
 * `sort`, `footer`, `caption`, `loading`, `empty`, per-column `align` /
 * `width` / `numeric` / `sortable`. Adding a table should never mean inventing
 * a header cell again.
 */
import React, { Fragment, useMemo } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { FOCUS_RING, type Tone } from '../mono/primitives';
import {
  ALIGN_CLASS,
  DENSITY_CELL,
  DENSITY_HEAD,
  accentShadow,
  type Align,
  type Density,
  type RuleStyle,
} from './foundation';

export interface Column<T> {
  /** Stable identity for the column; also the default sort key. */
  key: string;
  /** Header content. Omit with `showHeader={false}` for a headerless table. */
  header?: React.ReactNode;
  /** Cell renderer. Falls back to `String(row[key])` for plain records. */
  render?: (row: T, index: number) => React.ReactNode;
  align?: Align;
  /** Any CSS width — '2fr' is not valid here; use '40%', '12rem', 120. */
  width?: string | number;
  /** Right-aligns and applies tabular figures so digits line up in the column. */
  numeric?: boolean;
  sortable?: boolean;
  /** Sort key if it differs from `key`. */
  sortKey?: string;
  /** Hide without changing the columns array — for a column picker. */
  hidden?: boolean;
  /** Extra classes on every body cell in this column. */
  cellClassName?: string;
}

export interface SortSpec {
  key: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => string;

  /** ── Structure ── */
  /** Draw the header row. The one parameter every hand-rolled table hard-codes. */
  showHeader?: boolean;
  /** Accessible description; rendered visually unless `captionHidden`. */
  caption?: React.ReactNode;
  captionHidden?: boolean;
  /** Summary row rendered in a <tfoot>, above the bottom edge. */
  footer?: React.ReactNode;

  /** ── Skin ── */
  density?: Density;
  rule?: RuleStyle;
  zebra?: boolean;
  /** Wrap in a bordered panel. Off when the table sits inside an existing one. */
  bordered?: boolean;

  /** ── Behaviour ── */
  sort?: SortSpec | null;
  onSortChange?: (next: SortSpec) => void;
  onRowClick?: (row: T, index: number) => void;
  selectable?: boolean;
  selectedKeys?: Set<string>;
  onSelectionChange?: (next: Set<string>) => void;
  /** Left accent bar per row — the deck's signature move, for semantic state. */
  getRowAccent?: (row: T, index: number) => Tone | undefined;
  /** Content drawn in a full-width row directly under this row (null = none). Use for a row that opens in place. */
  renderExpanded?: (row: T, index: number) => React.ReactNode | null;

  /** ── Scroll ── */
  stickyHeader?: boolean;
  /** Required for `stickyHeader` to have anything to stick within. */
  maxHeight?: string | number;

  /** ── States ── */
  loading?: boolean;
  skeletonRows?: number;
  empty?: React.ReactNode;

  className?: string;
  /** Accessible name when there is no visible caption. */
  ariaLabel?: string;
}

const HEAD_TEXT =
  'font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)] font-normal';

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  showHeader = true,
  caption,
  captionHidden = false,
  footer,
  density = 'default',
  rule = 'horizontal',
  zebra = false,
  bordered = true,
  sort = null,
  onSortChange,
  onRowClick,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  getRowAccent,
  renderExpanded,
  stickyHeader = false,
  maxHeight,
  loading = false,
  skeletonRows = 5,
  empty = 'Nothing to show.',
  className = '',
  ariaLabel,
}: DataTableProps<T>) {
  const cols = useMemo(() => columns.filter((c) => !c.hidden), [columns]);
  const selected = selectedKeys ?? EMPTY_SET;
  const colCount = cols.length + (selectable ? 1 : 0);

  const cellRule =
    rule === 'none' ? '' : rule === 'grid' ? 'border-b border-r last:border-r-0' : 'border-b';
  const ruleStyle: React.CSSProperties =
    rule === 'none' ? {} : { borderColor: 'var(--rule)' };

  const allKeys = rows.map(getRowKey);
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selected.has(k));
  const someSelected = allKeys.some((k) => selected.has(k)) && !allSelected;

  const toggleAll = () => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? new Set() : new Set(allKeys));
  };
  const toggleOne = (key: string) => {
    if (!onSelectionChange) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange(next);
  };

  const headCell = (col: Column<T>) => {
    const align: Align = col.align ?? (col.numeric ? 'right' : 'left');
    const sortKey = col.sortKey ?? col.key;
    const isSorted = sort?.key === sortKey;
    const base = `${DENSITY_HEAD[density]} ${ALIGN_CLASS[align]} ${HEAD_TEXT} ${
      rule === 'none' ? '' : 'border-b'
    } ${rule === 'grid' ? 'border-r last:border-r-0' : ''}`;

    if (!col.sortable || !onSortChange) {
      return (
        <th
          key={col.key}
          scope="col"
          className={base}
          style={{ ...ruleStyle, width: col.width }}
        >
          {col.header}
        </th>
      );
    }

    const nextDirection: 'asc' | 'desc' =
      isSorted && sort?.direction === 'asc' ? 'desc' : 'asc';
    const Icon = !isSorted ? ChevronsUpDown : sort?.direction === 'asc' ? ChevronUp : ChevronDown;

    return (
      <th
        key={col.key}
        scope="col"
        aria-sort={isSorted ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`${base} p-0`}
        style={{ ...ruleStyle, width: col.width }}
      >
        <button
          type="button"
          onClick={() => onSortChange({ key: sortKey, direction: nextDirection })}
          className={`flex w-full items-center gap-1.5 ${DENSITY_HEAD[density]} ${
            align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''
          } transition-colors hover:text-[var(--text-pri)] ${FOCUS_RING}`}
        >
          {col.header}
          <Icon
            className={`h-3 w-3 shrink-0 ${isSorted ? 'text-[var(--mustard)]' : 'opacity-40'}`}
            aria-hidden="true"
          />
        </button>
      </th>
    );
  };

  const bodyCell = (col: Column<T>, row: T, index: number, accent: Tone | undefined, first: boolean) => {
    const align: Align = col.align ?? (col.numeric ? 'right' : 'left');
    return (
      <td
        key={col.key}
        className={`${DENSITY_CELL[density]} ${ALIGN_CLASS[align]} text-sm text-[var(--text-sec)] ${
          col.numeric ? 'tabular-nums' : ''
        } ${cellRule} ${col.cellClassName ?? ''}`}
        style={{ ...ruleStyle, ...(first ? accentShadow(accent) : undefined) }}
      >
        {col.render ? col.render(row, index) : defaultCell(row, col.key)}
      </td>
    );
  };

  const table = (
    <table
      className="w-full border-collapse text-left"
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
    >
      {caption && (
        <caption
          className={
            captionHidden
              ? 'sr-only'
              : `${DENSITY_HEAD[density]} text-left font-mono text-[0.72rem] uppercase tracking-[0.12em] text-[var(--text-ter)]`
          }
        >
          {caption}
        </caption>
      )}

      {showHeader && (
        <thead
          className={
            stickyHeader ? 'sticky top-0 z-10 bg-[var(--bg-raised)]' : 'bg-[var(--surface)]'
          }
        >
          <tr>
            {selectable && (
              <th scope="col" className={`${DENSITY_HEAD[density]} w-10 ${rule === 'none' ? '' : 'border-b'}`} style={ruleStyle}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  aria-label={allSelected ? 'Deselect all rows' : 'Select all rows'}
                  className={`h-3.5 w-3.5 cursor-pointer accent-[var(--slate)] ${FOCUS_RING}`}
                />
              </th>
            )}
            {cols.map(headCell)}
          </tr>
        </thead>
      )}

      <tbody>
        {loading &&
          Array.from({ length: skeletonRows }).map((_, i) => (
            <tr key={`skeleton-${i}`}>
              {selectable && <td className={`${DENSITY_CELL[density]} ${cellRule}`} style={ruleStyle} />}
              {cols.map((col) => (
                <td key={col.key} className={`${DENSITY_CELL[density]} ${cellRule}`} style={ruleStyle}>
                  <span className="block h-3 animate-pulse bg-[var(--hover-lift)]" />
                </td>
              ))}
            </tr>
          ))}

        {!loading && rows.length === 0 && (
          <tr>
            <td colSpan={colCount} className="px-4 py-12 text-center text-sm text-[var(--text-ter)]">
              {empty}
            </td>
          </tr>
        )}

        {!loading &&
          rows.map((row, index) => {
            const key = getRowKey(row, index);
            const accent = getRowAccent?.(row, index);
            const isSelected = selected.has(key);
            const expanded = renderExpanded?.(row, index) ?? null;
            return (
              <Fragment key={key}>
              <tr
                aria-expanded={renderExpanded ? expanded != null : undefined}
                onClick={onRowClick ? () => onRowClick(row, index) : undefined}
                className={[
                  zebra && index % 2 === 1 ? 'bg-[var(--hover-faint)]' : '',
                  isSelected ? 'bg-[var(--active-tint)]' : '',
                  onRowClick ? 'cursor-pointer transition-colors hover:bg-[var(--hover-tint)]' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {selectable && (
                  <td
                    className={`${DENSITY_CELL[density]} ${cellRule}`}
                    style={{ ...ruleStyle, ...accentShadow(accent) }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleOne(key)}
                      aria-label={`Select row ${index + 1}`}
                      className={`h-3.5 w-3.5 cursor-pointer accent-[var(--slate)] ${FOCUS_RING}`}
                    />
                  </td>
                )}
                {cols.map((col, ci) => bodyCell(col, row, index, accent, !selectable && ci === 0))}
              </tr>
              {expanded != null && (
                <tr>
                  <td colSpan={colCount} className={cellRule} style={ruleStyle}>
                    {expanded}
                  </td>
                </tr>
              )}
              </Fragment>
            );
          })}
      </tbody>

      {footer && (
        <tfoot>
          <tr className="bg-[var(--surface)]">
            <td
              colSpan={colCount}
              className={`${DENSITY_CELL[density]} border-t text-sm text-[var(--text-sec)]`}
              style={{ borderColor: 'var(--rule)' }}
            >
              {footer}
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );

  return (
    <div
      className={`w-full overflow-x-auto ${maxHeight ? 'overflow-y-auto' : ''} ${
        bordered ? 'border bg-[var(--bg-raised)]' : ''
      } ${className}`}
      style={{ ...(bordered ? { borderColor: 'var(--rule)' } : {}), maxHeight }}
    >
      {table}
    </div>
  );
}

const EMPTY_SET: Set<string> = new Set();

function defaultCell<T>(row: T, key: string): React.ReactNode {
  const value = (row as Record<string, unknown>)[key];
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return null;
}
